// Captures the microphone, runs an AudioWorklet that resamples to 16 kHz int16
// PCM, and forwards each chunk to the on-chunk callback.

export class MicCapture {
  constructor(sampleRate) {
    this.targetRate = sampleRate;
    this.ctx = null;
    this.stream = null;
    this.node = null;
    this.source = null;
    this.muted = true;
    this.onChunk = null;
  }

  async start() {
    if (this.stream) return;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    this.setMuted(this.muted);

    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this.ctx.state === "suspended") await this.ctx.resume();

    // Resolve the worklet path relative to this module so the same code
    // works whether the page was served at `/`, at `/static/`, at a Vite
    // dev server on a different host, or from a built `dist/` bundle.
    const workletUrl = new URL("../recorder-worklet.js", import.meta.url);
    await this.ctx.audioWorklet.addModule(workletUrl);
    this.node = new AudioWorkletNode(this.ctx, "recorder-processor", {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      processorOptions: {
        targetSampleRate: this.targetRate,
        chunkMs: 40,
      },
    });
    this.node.port.onmessage = (ev) => {
      if (this.muted) return;
      if (this.onChunk) this.onChunk(ev.data);
    };

    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.source.connect(this.node);
  }

  setMuted(muted) {
    this.muted = muted;
    if (this.stream) {
      for (const t of this.stream.getAudioTracks()) t.enabled = !muted;
    }
  }

  async stop() {
    this.onChunk = null;
    try {
      if (this.source) this.source.disconnect();
    } catch (_) {}
    try {
      if (this.node) this.node.disconnect();
    } catch (_) {}
    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop();
    }
    if (this.ctx) {
      try {
        await this.ctx.close();
      } catch (_) {}
    }
    this.source = null;
    this.node = null;
    this.stream = null;
    this.ctx = null;
  }
}
